import React, { createContext, useCallback, useState, ReactNode } from 'react';

export interface ActiveTransfer {
  id: string;
  type: 'upload' | 'download';
  startedAt: Date;
  bucket: string;
  key: string;
}

interface TransferContextType {
  isTransferActive: boolean;
  registerTransfer: (transfer: Omit<ActiveTransfer, 'startedAt'>) => () => void;
  getActiveCount: (type: 'upload' | 'download') => number;
}

const TransferContext = createContext<TransferContextType | undefined>(undefined);

export function TransferProvider({ children }: { children: ReactNode }) {
  const [uploads, setUploads] = useState<Map<string, ActiveTransfer>>(new Map());
  const [downloads, setDownloads] = useState<Map<string, ActiveTransfer>>(new Map());

  const isTransferActive = uploads.size + downloads.size > 0;

  const registerTransfer = useCallback(
    (transfer: Omit<ActiveTransfer, 'startedAt'>): (() => void) => {
      const fullTransfer: ActiveTransfer = {
        ...transfer,
        startedAt: new Date(),
      };

      if (transfer.type === 'upload') {
        setUploads((prev) => new Map(prev).set(transfer.id, fullTransfer));
      } else {
        setDownloads((prev) => new Map(prev).set(transfer.id, fullTransfer));
      }

      return () => {
        if (transfer.type === 'upload') {
          setUploads((prev) => {
            const m = new Map(prev);
            m.delete(transfer.id);
            return m;
          });
        } else {
          setDownloads((prev) => {
            const m = new Map(prev);
            m.delete(transfer.id);
            return m;
          });
        }
      };
    },
    []
  );

  const getActiveCount = useCallback(
    (type: 'upload' | 'download'): number => {
      return type === 'upload' ? uploads.size : downloads.size;
    },
    [uploads, downloads]
  );

  return (
    <TransferContext.Provider value={{ isTransferActive, registerTransfer, getActiveCount }}>
      {children}
    </TransferContext.Provider>
  );
}

export function useTransfer(): TransferContextType {
  const context = React.useContext(TransferContext);
  if (context === undefined) {
    throw new Error('useTransfer must be used within a TransferProvider');
  }
  return context;
}
